import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useImage } from '../../context/ImageContext';
import InfoTooltip from '../common/InfoTooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';


const AdjustPositionDropdown = ({ containerRef, imageRef, mySelectedImage, isOpen, setIsOpen, aspectRatio, setReLight, setBackdropOnly }) => {
  const { selectedImage, setSelectedImage } = useImage();
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setSelectedImage(mySelectedImage);
  }, [mySelectedImage]);

  const resetImagePosition = () => {
    setPosition({ x: 0, y: 0 });
    setScale(1);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    setStartPos({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  }, [position.x, position.y]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const newX = e.clientX - startPos.x;
    const newY = e.clientY - startPos.y;

    // Calculate bounds based on image size and scale
    if (imageRef.current && imageRef.current.naturalWidth && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const imageWidth = imageRef.current.naturalWidth * scale;
      const imageHeight = imageRef.current.naturalHeight * scale;
      const maxX = Math.max(0, (imageWidth - rect.width) / 2);
      const maxY = Math.max(0, (imageHeight - rect.height) / 2);

      setPosition({
        x: Math.max(-maxX, Math.min(maxX, newX)),
        y: Math.max(-maxY, Math.min(maxY, newY))
      });
    }
  }, [isDragging, startPos.x, startPos.y, scale, imageRef, containerRef]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e) => {
    if (e.target === imageRef.current || e.target === containerRef.current) {
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY > 0 ? -0.05 : 0.05;
      const newScale = Math.max(0.2, Math.min(3, scale + delta));

      // Adjust position to keep image centered when scaling
      if (newScale !== scale) {
        const scaleRatio = newScale / scale;
        setPosition(prevPosition => ({
          x: prevPosition.x * scaleRatio,
          y: prevPosition.y * scaleRatio
        }));
        setScale(newScale);
      }
    }
  }, [scale, imageRef, containerRef]);

  // Set up event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        container.removeEventListener('wheel', handleWheel);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [handleWheel, handleMouseMove, handleMouseUp, containerRef]);

  // Handle image load
  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  // Reset position and scale when image changes
  useEffect(() => {
    // setIsOpen(selectedImage.alt !== 'No Product Selected');
    setPosition({ x: 0, y: 0 });
    setScale(1);
    setImageLoaded(false);
  }, [selectedImage]);

  // Get aspect ratio display name
  const getAspectRatioName = (ratio) => {
    const ratioMap = {
      [1/1]: '1:1 (Square)',
      [16/9]: '16:9 (Widescreen)',
      [4/3]: '4:3 (Standard)',
      [3/4]: '3:4 (Portrait)',
      [9/16]: '9:16 (Mobile)'
    };
    return ratioMap[ratio] || `${ratio.toFixed(2)}:1`;
  };

  return (
    <div className="flex bg-gray-100">
      <div className="bg-white rounded-lg p-3 w-full flex flex-col items-center">
        <div className="w-full">
          <div
            className="flex justify-between items-center cursor-pointer w-full"

          >
            <div className="flex items-center">
              <span className="text-gray-800 text-sm flex-1 text-left pr-2">Adjust Position</span>
              <InfoTooltip iconSize={12}>
                Modify the location and scale of the product image. Ensure the product remains within the designated area marked by the dotted line.
                <br /><br />
                <strong>Current Aspect Ratio: {getAspectRatioName(aspectRatio)}</strong>
              </InfoTooltip>
            </div>
            <div className="flex items-center">
              {selectedImage && selectedImage.alt !== 'No Product Selected' && (
                <i
                  className="fas fa-undo-alt text-purple-600 hover:text-purple-700 mr-3 text-sm cursor-pointer transition-colors duration-200"
                  title="Reset position"
                  onClick={(e) => {
                    e.stopPropagation();
                    resetImagePosition();
                  }}
                ></i>
              )}
           
            </div>
          </div>
        </div>

        {isOpen && (
          <div className="w-full">
            <div
              ref={containerRef}
              id="dropdown-content"
              className="mt-4 border border-purple-500 border-dashed rounded-lg flex justify-center items-center relative overflow-hidden mx-auto"
              style={{
                width: '100%',
                aspectRatio: aspectRatio,
                maxWidth: '300px',
                margin: '1rem auto',
              }}
            >
              {selectedImage && selectedImage.alt !== 'No Product Selected' ? (
                <img
                  ref={imageRef}
                  id="adjustable-image"
                  alt={selectedImage.alt}
                  className="w-full h-full object-contain cursor-move"
                  src={selectedImage.src}
                  style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                    transition: isDragging ? 'none' : 'transform 0.1s ease',
                    maxWidth: '100%',
                    maxHeight: '100%'
                  }}
                  onMouseDown={handleMouseDown}
                  onLoad={handleImageLoad}
                />
              ) : (
                <div className="text-gray-500 text-center">
                  <p>No image selected</p>
                  <p className="text-sm">Select an image from the Products section</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdjustPositionDropdown;
